/*
This file is located in the main branch
Last time the file was updated (UTC): [11.12.2024 08:50:34]
Permissions to update outside the main branch: [true]
*/

import {
    DropDown,
    DynamicListAddButton,
    DynamicListWrapperProps,
    requiredRule,
    useErrorState,
    useUpdateEffect,
    ValidatedDropDownList,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getSelectList } from 'axios/dictionaryApi';
import { getQuestionaryAll } from 'axios/questionaryApi';
import { getTariffGroupsSelect } from 'axios/tariffGroupApi';
import { getTradeOrgsList } from 'axios/tradeOrgsApi';
import useApi from 'hooks/useApi';
import { Outlet, OutletGroup } from 'models/api/outlet';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, Row, Table } from 'reactstrap';
import GetOptions from 'utils/options';
import { OutletFormType } from './OutletChangeModal';

type TabHomeProps = {
    isOpen?: boolean;
    formHasRole: boolean;
    showAdditionalSettings: boolean;
    merchantRegions: DropDown[];
    typeWorks: DropDown[];
    isOutletDictsFetching: boolean;
    brandCode: DropDown[];
    isFetchingBrandCodeTypes: boolean;
};

const TabHome = ({
    isOpen = false,
    formHasRole,
    showAdditionalSettings,
    merchantRegions,
    typeWorks,
    isOutletDictsFetching,
    brandCode,
    isFetchingBrandCodeTypes,
}: TabHomeProps) => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const { handleRequestError } = useErrorState();
    const { watch, setValue } = useFormContext<OutletFormType>();
    const tradeOrgId = watch('tradeOrgId');
    const typeWork = watch('typeWork');

    const [hasOpen, setHasOpen] = useState(isOpen);
    useEffect(() => {
        setHasOpen((val) => val || isOpen);
    }, [isOpen]);

    useUpdateEffect(() => {
        setTimeout(() => setValue('tariffs', [{ tariffGroupId: null, initialFeeId: null }]));
    }, [tradeOrgId]);

    const { isFetching: tradeOrgsFetching, data: tradeOrgs } = useQuery(
        ['tradeOrgs'],
        () => getTradeOrgsList(api),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const { isFetching: isIntegrationTypesFetching, data: outletDicts } = useQuery(
        ['TOIntegrationTypes', i18n.language],
        () => getSelectList(api, ['TOIntegrationType'], i18n.language),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const { isFetching: tariffGroupsFetching, data: tariffGroups } = useQuery(
        ['TariffGroupsByTradeOrg'],
        () => getTariffGroupsSelect(api),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const { isFetching: questionariesFetching, data: questionaries } = useQuery(
        ['questionaries'],
        () => getQuestionaryAll(api),
        {
            enabled: hasOpen,
            onError: handleRequestError,
        }
    );

    const formComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <Form
                {...props}
                tariffGroups={tariffGroups || []}
                tariffGroupsFetching={tariffGroupsFetching}
                formHasRole={formHasRole}
            />
        ),
        [tariffGroups, tariffGroupsFetching, formHasRole]
    );

    const wrapper = useCallback(
        ({ children, ...props }: DynamicListWrapperProps) => (
            <Table bordered>
                <thead>
                    <tr className="align-middle">
                        <th className="w-90">
                            <Trans i18nKey="BackOfficeTariffGroup" />
                        </th>
                        <th className="w-10 text-center">
                            <DynamicListAddButton {...props} />
                        </th>
                    </tr>
                </thead>
                <tbody>{children}</tbody>
            </Table>
        ),
        []
    );

    const discountRule = (val: number) => {
        const discountMinValue = 1;
        const discountMaxValue = 99;

        if (val < discountMinValue || val > discountMaxValue)
            return t('BackOfficeRangeValidator', {
                ComparisonValueMin: discountMinValue.toString(),
                ComparisonValueMax: discountMaxValue.toString(),
            });

        return true;
    };

    return (
        <div className="my-3">
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        id="outletIsActive"
                        type="checkbox"
                        name="isActive"
                        disabled={!formHasRole}
                        label={t('BackOfficeIsActive')}
                    />
                </Col>
            </Row>
            <hr />
            <Row>
                <Col xs="4">
                    <ValidatedInput<OutletFormType>
                        name="code"
                        label={t('BackOfficeCode')}
                        disabled={!formHasRole}
                    />
                </Col>
                <Col>
                    <ValidatedInput<OutletFormType>
                        type="select"
                        name="tradeOrgId"
                        label={t('BackOfficeTradeOrg')}
                        disabled={!formHasRole}
                        rules={{
                            required: requiredRule,
                        }}
                        options={[<option key={-1} value="" />].concat(
                            (tradeOrgs || []).map((x) => (
                                <option key={x.value} value={x.value}>
                                    {x.text}
                                </option>
                            ))
                        )}
                        loading={tradeOrgsFetching}
                    />
                </Col>
            </Row>
            <Row>
                {showAdditionalSettings && (
                    <Col xs="4">
                        <ValidatedInput<OutletFormType>
                            type="select"
                            name="integrationType"
                            label={t('BackOfficeIntegrationType')}
                            disabled={!formHasRole}
                            loading={isIntegrationTypesFetching}
                            options={GetOptions(
                                outletDicts ? outletDicts['TOIntegrationType'] : [],
                                true,
                                false
                            )}
                        />
                    </Col>
                )}
                <Col xs="4">
                    <ValidatedDropDownList<OutletFormType>
                        name="typeWork"
                        items={typeWorks}
                        isLoading={isOutletDictsFetching}
                        disabled={!formHasRole && !typeWorks.length}
                        label={t('BackOfficeTypeWork')}
                        rules={{ required: requiredRule }}
                    />
                </Col>
                {typeWork === 'C' && (
                    <Col xs="4">
                        <ValidatedInput<OutletFormType>
                            type="number"
                            name="discount"
                            label={t('BackOfficeDiscount')}
                            disabled={!formHasRole}
                            rules={{
                                required: requiredRule,
                                validate: discountRule,
                            }}
                        />
                    </Col>
                )}
                <Col xs="6">
                    <ValidatedDropDownList<OutletFormType>
                        name="brandCode"
                        items={brandCode}
                        isLoading={!isFetchingBrandCodeTypes}
                        label={t('BackOfficeBrandCode')}
                        rules={{ required: requiredRule }}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs="6">
                    <ValidatedInput<OutletFormType>
                        type="number"
                        name="pinfl"
                        label={t('BackOfficePinfl')}
                        maskProps={{ mask: '999 999 999 999 99' }}
                    />
                </Col>
                <Col xs="6">
                    <ValidatedInput<OutletFormType>
                        type="number"
                        name="inn"
                        label={t('BackOfficeInn')}
                        maskProps={{ mask: '999 999 999' }}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs="4">
                    <ValidatedDropDownList<OutletFormType>
                        name="region"
                        items={merchantRegions}
                        isLoading={isOutletDictsFetching}
                        disabled={!formHasRole && !merchantRegions.length}
                        label={t('BackOfficeRegion')}
                    />
                </Col>
                <Col>
                    <ValidatedInput<OutletFormType>
                        name="address"
                        label={t('BackOfficeAddress')}
                        disabled={!formHasRole}
                        rules={{
                            required: requiredRule,
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput<OutletFormType>
                        type="number"
                        name="lat"
                        label={t('BackOfficeLatitude')}
                        disabled={!formHasRole}
                    />
                </Col>
                <Col>
                    <ValidatedInput<OutletFormType>
                        type="number"
                        name="lng"
                        label={t('BackOfficeLongitude')}
                        disabled={!formHasRole}
                    />
                </Col>
            </Row>
            {showAdditionalSettings && (
                <Row>
                    <Col>
                        <ValidatedInput<OutletFormType>
                            type="select"
                            name="questionaryId"
                            label={t('BackOfficeClientQuestionary')}
                            disabled={!formHasRole}
                            loading={questionariesFetching}
                            options={[<option key={-1} value="" />].concat(
                                (questionaries || []).map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}
                                    </option>
                                ))
                            )}
                        />
                    </Col>
                </Row>
            )}
            <ValidatedDynamicList<Outlet, OutletGroup>
                formComponent={formComponent}
                wrapper={wrapper}
                prepend={false}
                emptyRecord={{
                    tariffGroupId: null,
                    initialFeeId: null,
                }}
                name="tariffs"
                compareFunc={(a, b) =>
                    a?.tariffGroupId?.toString() === b?.tariffGroupId?.toString()
                }
                disabled={!formHasRole}
            />
            <ValidatedInput<OutletFormType>
                id="info-dsc-field"
                name="dsc"
                label={t('BackOfficeDescription')}
                type="textarea"
                disabled={!formHasRole}
            />
        </div>
    );
};

const Form = ({
    idx,
    fieldId,
    checkDouble,
    tariffGroups,
    tariffGroupsFetching,
    formHasRole,
}: ValidatedDynamicListFormComponentProps & {
    tariffGroups: DropDown[];
    tariffGroupsFetching: boolean;
    formHasRole: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <td>
            <ValidatedInput<OutletFormType>
                type="select"
                name={`tariffs.${idx}.tariffGroupId`}
                label={t('BackOfficeName')}
                disabled={!formHasRole}
                rules={{
                    required: requiredRule,
                    validate: () => checkDouble(fieldId) ?? undefined,
                }}
                loading={tariffGroupsFetching}
                options={GetOptions(tariffGroups, true, false)}
            />
        </td>
    );
};

export default TabHome;
